import { useLingui } from '@lingui/react/macro';
import classNames from 'classnames';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import React, { KeyboardEvent, useEffect, useRef, useState } from 'react';
import UDIconButton from 'ud-ui/components/icon-button';
import UDText from 'ud-ui/components/text';

/** Ровно те поля `renderCustomHeader`, которые нужны шапке, плюс границы календаря. */
export interface CalendarHeaderProps {
  monthDate: Date;
  decreaseMonth: () => void;
  increaseMonth: () => void;
  changeMonth: (month: number) => void;
  changeYear: (year: number) => void;
  prevMonthButtonDisabled: boolean;
  nextMonthButtonDisabled: boolean;
  minDate?: Date | null;
  maxDate?: Date | null;
}

/**
 * Вид календаря: дни месяца → список месяцев года → список годов. Как в MUI и Material 3:
 * заголовок — кнопка переключения вида, стрелка у него поворачивается, когда открыт список.
 */
type CalendarView = 'days' | 'months' | 'years';

/** Лист годов — 12 штук, сетка 3 × 4, как у месяцев: обе сетки одного размера, календарь не меняет высоту. */
const YEARS_PER_PAGE = 12;
const MONTHS = Array.from({ length: 12 }, (_, month) => month);

const capitalize = (text: string, locale: string) => `${text.charAt(0).toLocaleUpperCase(locale)}${text.slice(1)}`;
const yearPageStart = (year: number) => year - (((year % YEARS_PER_PAGE) + YEARS_PER_PAGE) % YEARS_PER_PAGE);

/**
 * Шапка календаря: «Октябрь 2026», стрелки и переключение видов. Клик по заголовку открывает
 * список месяцев с годом наверху; клик по году — список годов. Выбор года возвращает к месяцам,
 * выбор месяца — к дням этого месяца.
 *
 * Список месяцев и годов накрывает сетку дней (`.ud-calendar-panel` в `calendar-styles.ts`), а сами
 * дни на это время скрыты — из фокуса и для скринридера тоже. Месяцы и годы целиком вне
 * `minDate`/`maxDate` недоступны, как и дни.
 *
 * Месяц берётся из `Intl` текущего языка Lingui — тот же язык, что `activateLocale` выставляет
 * react-datepicker. Месяц без года в `Intl` даёт именительный падеж («октябрь», а не «октября»),
 * год дописываем сами, чтобы не получить русское «2026 г.».
 */
const CalendarHeader = (props: CalendarHeaderProps) => {
  const {
    monthDate,
    decreaseMonth,
    increaseMonth,
    changeMonth,
    changeYear,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
    minDate,
    maxDate,
  } = props;
  const { t, i18n } = useLingui();
  const [view, setView] = useState<CalendarView>('days');
  // Год, который листается в списке месяцев, и первый год листа в списке годов.
  const [year, setYear] = useState(monthDate.getFullYear());
  const [pageStart, setPageStart] = useState(yearPageStart(monthDate.getFullYear()));
  const panelRef = useRef<HTMLDivElement>(null);

  const today = new Date();
  const minYear = minDate?.getFullYear() ?? -Infinity;
  const maxYear = maxDate?.getFullYear() ?? Infinity;

  const monthName = (month: number) => capitalize(i18n.date(new Date(2000, month, 1), { month: 'long' }), i18n.locale);

  const isMonthDisabled = (month: number) => {
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0, 23, 59, 59);
    return Boolean((minDate && last < minDate) || (maxDate && first > maxDate));
  };

  // Открыли список — фокус на текущем пункте: с клавиатуры не нужно добираться до него Tab-ом.
  useEffect(() => {
    if (view === 'days') return;
    panelRef.current?.querySelector<HTMLButtonElement>('[data-current="true"]:not(:disabled)')?.focus({ preventScroll: true });
  }, [view]);

  const openMonths = () => {
    setYear(monthDate.getFullYear());
    setView('months');
  };

  const openYears = () => {
    setPageStart(yearPageStart(year));
    setView('years');
  };

  const pickMonth = (month: number) => {
    changeYear(year);
    changeMonth(month);
    setView('days');
  };

  const pickYear = (picked: number) => {
    setYear(picked);
    setView('months');
  };

  // Esc в списке возвращает к дням, а не закрывает весь календарь: так ведёт себя вложенное меню.
  const handlePanelKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape') return;
    event.stopPropagation();
    event.preventDefault();
    setView('days');
  };

  let title: string;
  let switchLabel: string;
  let onSwitch: () => void;
  let onPrev: () => void;
  let onNext: () => void;
  let prevDisabled: boolean;
  let nextDisabled: boolean;
  let prevLabel: string;
  let nextLabel: string;

  if (view === 'days') {
    title = `${monthName(monthDate.getMonth())} ${monthDate.getFullYear()}`;
    switchLabel = t`Выбрать месяц и год`;
    onSwitch = openMonths;
    onPrev = decreaseMonth;
    onNext = increaseMonth;
    prevDisabled = prevMonthButtonDisabled;
    nextDisabled = nextMonthButtonDisabled;
    prevLabel = t`Предыдущий месяц`;
    nextLabel = t`Следующий месяц`;
  } else if (view === 'months') {
    title = String(year);
    switchLabel = t`Выбрать год`;
    onSwitch = openYears;
    onPrev = () => setYear(year - 1);
    onNext = () => setYear(year + 1);
    prevDisabled = year - 1 < minYear;
    nextDisabled = year + 1 > maxYear;
    prevLabel = t`Предыдущий год`;
    nextLabel = t`Следующий год`;
  } else {
    title = `${pageStart} – ${pageStart + YEARS_PER_PAGE - 1}`;
    switchLabel = t`Вернуться к месяцам`;
    onSwitch = () => setView('months');
    onPrev = () => setPageStart(pageStart - YEARS_PER_PAGE);
    onNext = () => setPageStart(pageStart + YEARS_PER_PAGE);
    prevDisabled = pageStart - 1 < minYear;
    nextDisabled = pageStart + YEARS_PER_PAGE > maxYear;
    prevLabel = t`Предыдущие годы`;
    nextLabel = t`Следующие годы`;
  }

  return (
    <>
      <div className="flex items-center justify-between">
        <UDIconButton
          variant="ghost"
          size="sm"
          icon={<ChevronLeft />}
          aria-label={prevLabel}
          disabled={prevDisabled}
          onClick={onPrev}
        />
        <button
          type="button"
          className={classNames('ud-calendar-switch', { 'is-open': view !== 'days' })}
          aria-label={`${title}. ${switchLabel}`}
          aria-expanded={view !== 'days'}
          onClick={onSwitch}
        >
          <UDText variant="body-3" as="span" aria-live="polite">
            {title}
          </UDText>
          <ChevronDown size={16} aria-hidden />
        </button>
        <UDIconButton
          variant="ghost"
          size="sm"
          icon={<ChevronRight />}
          aria-label={nextLabel}
          disabled={nextDisabled}
          onClick={onNext}
        />
      </div>

      {view === 'months' && (
        <div ref={panelRef} className="ud-calendar-panel" role="group" aria-label={t`Месяцы`} onKeyDown={handlePanelKeyDown}>
          {MONTHS.map((month) => {
            const isCurrent = month === monthDate.getMonth() && year === monthDate.getFullYear();
            const isToday = month === today.getMonth() && year === today.getFullYear();
            return (
              <button
                key={month}
                type="button"
                className={classNames('ud-calendar-cell', { 'is-current': isCurrent, 'is-today': isToday })}
                data-current={isCurrent}
                aria-pressed={isCurrent}
                disabled={isMonthDisabled(month)}
                onClick={() => pickMonth(month)}
              >
                {monthName(month)}
              </button>
            );
          })}
        </div>
      )}

      {view === 'years' && (
        <div ref={panelRef} className="ud-calendar-panel" role="group" aria-label={t`Годы`} onKeyDown={handlePanelKeyDown}>
          {Array.from({ length: YEARS_PER_PAGE }, (_, index) => pageStart + index).map((pageYear) => {
            const isCurrent = pageYear === year;
            return (
              <button
                key={pageYear}
                type="button"
                className={classNames('ud-calendar-cell', { 'is-current': isCurrent, 'is-today': pageYear === today.getFullYear() })}
                data-current={isCurrent}
                aria-pressed={isCurrent}
                disabled={pageYear < minYear || pageYear > maxYear}
                onClick={() => pickYear(pageYear)}
              >
                {pageYear}
              </button>
            );
          })}
        </div>
      )}
    </>
  );
};

export default CalendarHeader;
